import { components } from "@/__openapi__/backend/schema";
import {
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextOverflow,
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSUserAvatar,
  MDSChip,
  MDSChipVariant,
  MDSMenuItemVariant,
  MDSMenuItemIconVariant,
} from "@/mds";
import { ThemeType, useTheme } from "@/mds/themes";
import { MemCommonEditorTheme } from "@mem-labs/common-editor";
import { formatCalendarEventDate, formatCalendarEventTime } from "@/domains/calendar/formatting";
import { windowModule } from "@/modules/window";
import {
  StyledContainer,
  StyledLabel,
  StyledRow,
  StyledValue,
  StyledDescriptionContainer,
  StyledDescriptionMarkdown,
  StyledShowMore,
  StyledGuestsContainer,
  StyledGuestsHeader,
  StyledGuestMenuItem,
  StyledAttachmentsContainer,
  ButtonContainer,
} from "./floating-calendar-event-preview.style";
import { useState } from "react";
import {
  getMeetingPlatformFromUrl,
  getMeetingProvider,
  getCalendarProviderFromUrl,
  MeetingPlatform,
  CalendarProvider,
} from "@/domains/calendar/getMeetingPlatformFromUrl";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";

const getMeetingIconKind = (meetLink: string | null | undefined): MDSIconKind => {
  const platform = getMeetingPlatformFromUrl(meetLink);
  switch (platform) {
    case MeetingPlatform.GoogleMeet:
      return MDSIconKind.GoogleMeetSmall;
    case MeetingPlatform.Zoom:
      return MDSIconKind.ZoomSmall;
    default:
      return MDSIconKind.Video;
  }
};

const getCalendarIconKind = (htmlLink: string | null | undefined): MDSIconKind => {
  const provider = getCalendarProviderFromUrl(htmlLink);
  switch (provider) {
    case CalendarProvider.Google:
      return MDSIconKind.GoogleCalendar;
    case CalendarProvider.Outlook:
      return MDSIconKind.OutlookCalendar;
    default:
      return MDSIconKind.Calendar;
  }
};

type CalendarEventOrganizer = components["schemas"]["CalendarEventOrganizer"];
type CalendarEventAttendee = components["schemas"]["CalendarEventAttendee"];
type CalendarEventAttachment = components["schemas"]["CalendarEventAttachment"];

interface FloatingCalendarEventPreviewViewProps {
  startTime?: string;
  endTime?: string;
  description?: string;
  isAllDay: boolean;
  host?: CalendarEventOrganizer | null;
  guests?: CalendarEventAttendee[];
  attachments?: CalendarEventAttachment[];
  htmlLink?: string | null;
  meetLink?: string | null;
  connectedCalendarEventId: string;
}

export const FloatingCalendarEventPreviewView = ({
  startTime,
  endTime,
  description,
  isAllDay,
  host,
  guests,
  attachments,
  htmlLink,
  meetLink,
  connectedCalendarEventId,
}: FloatingCalendarEventPreviewViewProps) => {
  const [showMultilineDescription, setShowMultilineDescription] = useState<boolean>(false);
  const [isGuestsCollapsed, setIsGuestsCollapsed] = useState<boolean>(false);
  const { themeType } = useTheme();
  const timeText = formatCalendarEventTime(startTime, endTime, isAllDay);
  const dateText = startTime ? formatCalendarEventDate(startTime) : isAllDay ? "All day" : "";
  const hostDisplayName = host?.display_name || host?.email;

  // No-op handler - links are opened in new tab via openLinksInNewTab prop
  const handleClickMemItem = () => {};

  return (
    <StyledContainer>
      {dateText && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Date
          </StyledLabel>
          <StyledValue
            variant={MDSTextVariant.TextS}
            color={MDSTextColor.Primary}
            numberOfLines={1}
            overflow={MDSTextOverflow.Ellipsis}
          >
            {dateText}
          </StyledValue>
        </StyledRow>
      )}

      {timeText && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Time
          </StyledLabel>
          <StyledValue
            variant={MDSTextVariant.TextS}
            color={MDSTextColor.Primary}
            numberOfLines={1}
            overflow={MDSTextOverflow.Ellipsis}
          >
            {timeText}
          </StyledValue>
        </StyledRow>
      )}

      {description && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Description
          </StyledLabel>
          <StyledDescriptionContainer>
            <StyledDescriptionMarkdown
              onClickMemItem={handleClickMemItem}
              openLinksInNewTab
              uiTheme={
                themeType === ThemeType.Harvest
                  ? MemCommonEditorTheme.Harvest
                  : MemCommonEditorTheme.HarvestMoon
              }
              $collapsed={!showMultilineDescription}
            >
              {description}
            </StyledDescriptionMarkdown>
            <StyledShowMore
              variant={MDSTextVariant.TextXs}
              weight={MDSTextWeight.Medium}
              color={MDSTextColor.Secondary}
              onClick={() => setShowMultilineDescription(!showMultilineDescription)}
            >
              Show {showMultilineDescription ? "less" : "more"}
            </StyledShowMore>
          </StyledDescriptionContainer>
        </StyledRow>
      )}

      {attachments && attachments.length > 0 && (
        <StyledRow align="center">
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Attachments
          </StyledLabel>
          <StyledValue variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
            <StyledAttachmentsContainer>
              {attachments.map((attachment, index) => (
                <MDSChip
                  key={index}
                  onClick={
                    attachment.file_url
                      ? () => windowModule.openInNewTab({ url: new URL(attachment.file_url ?? "") })
                      : undefined
                  }
                  variant={MDSChipVariant.Medium}
                  icon={MDSIconKind.File}
                >
                  {attachment.title || "Attachment"}
                </MDSChip>
              ))}
            </StyledAttachmentsContainer>
          </StyledValue>
        </StyledRow>
      )}

      {host && (
        <StyledRow align="center">
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Organizer
          </StyledLabel>
          <StyledValue variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
            <StyledGuestMenuItem
              id="host"
              label={hostDisplayName ?? ""}
              subtitle={host.email}
              variant={MDSMenuItemVariant.TwoLine}
              icon={() => <MDSUserAvatar displayName={hostDisplayName} size={24} />}
              iconVariant={MDSMenuItemIconVariant.Avatar}
            />
          </StyledValue>
        </StyledRow>
      )}

      {guests && guests.length > 0 && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Guests
          </StyledLabel>
          <StyledValue variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
            <StyledGuestsHeader onClick={() => setIsGuestsCollapsed(!isGuestsCollapsed)}>
              <MDSIcon
                kind={isGuestsCollapsed ? MDSIconKind.CaretRight : MDSIconKind.CaretDown}
                color={MDSIconColor.Secondary}
                size={11}
              />
              <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
                {guests.length} {guests.length === 1 ? "guest" : "guests"}
              </MDSText>
            </StyledGuestsHeader>
            {!isGuestsCollapsed && (
              <StyledGuestsContainer>
                {guests.map((guest, index) => {
                  const displayName = (guest.display_name || guest.email) ?? undefined;
                  return (
                    <StyledGuestMenuItem
                      key={index}
                      id={`guest-${index}`}
                      label={displayName ?? ""}
                      subtitle={guest.email}
                      variant={MDSMenuItemVariant.TwoLine}
                      icon={() => <MDSUserAvatar displayName={displayName} size={24} />}
                      iconVariant={MDSMenuItemIconVariant.Avatar}
                    />
                  );
                })}
              </StyledGuestsContainer>
            )}
          </StyledValue>
        </StyledRow>
      )}

      {(meetLink || htmlLink) && (
        <ButtonContainer>
          {htmlLink && (
            <MDSButton
              onClick={() => {
                trackEvent(TrackedEvent.CalendarEventOpen, {
                  connected_calendar_event_id: connectedCalendarEventId,
                });
                windowModule.openInNewTab({ url: new URL(htmlLink) });
              }}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={getCalendarIconKind(htmlLink)} />
              Open in calendar
            </MDSButton>
          )}
          {meetLink && (
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onClick={() => {
                trackEvent(TrackedEvent.CalendarEventJoin, {
                  connected_calendar_event_id: connectedCalendarEventId,
                  meeting_provider: getMeetingProvider(meetLink),
                });
                windowModule.openInNewTab({ url: new URL(meetLink) });
              }}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={getMeetingIconKind(meetLink)} />
              Join meeting
            </MDSButton>
          )}
        </ButtonContainer>
      )}
    </StyledContainer>
  );
};
